import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useIsFeatureFlag } from 'app/components/Common/FeatureFlag';
import { FEATURES } from 'types/Feature';
import { useHistory } from 'react-router-dom';
import { isUserLoggedIn } from 'utils/auth';
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace';

import HomeIcon from '@mui/icons-material/Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import {
  Article,
  ArticleOutlined,
  Assessment,
  AssessmentOutlined,
  CandlestickChart,
  CandlestickChartOutlined,
  Description,
  DomainAdd,
  DomainAddOutlined,
  Launch,
  Link,
  LinkOutlined,
  QueryStats,
  QueryStatsOutlined,
  Sensors,
  Settings,
  SettingsOutlined,
  TipsAndUpdates,
  TipsAndUpdatesOutlined,
  SmartToy,
  SmartToyOutlined,
} from '@mui/icons-material';
import { Badge, Box, Divider, Fade, Tooltip } from '@mui/material';
import MuiChip from 'app/design/uiComponents/MuiChip';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import WidgetsIcon from '@mui/icons-material/Widgets';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import StoreIcon from '@mui/icons-material/Store';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import PeopleIcon from '@mui/icons-material/People';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import LoginIcon from '@mui/icons-material/Login';
import LockIcon from '@mui/icons-material/Lock';
import { memo, useContext, useEffect, useState } from 'react';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { themeContext } from 'app/components/Context/ToggleThemeContext';
import { useTheme } from '@mui/material/styles';
import { openInNewTab, toBoolean } from 'utils/GenericFunctions';
import { styled, keyframes } from '@mui/material/styles';
import useGetSiteInfo from 'services/common/useGetSiteInfo';

const SideBarNavigationMenu = ({
  open,
  activeNav,
  loginModal,
}: {
  open: boolean;
  activeNav: string;
  loginModal: (isLogin: boolean) => void;
}) => {
  const history = useHistory();
  const isLoggedIn = isUserLoggedIn();
  const isTraders = useIsFeatureFlag([FEATURES.IS_TRADERS]);
  const isDiscover = useIsFeatureFlag([FEATURES.IS_DISCOVER]);
  const isOptionChain = useIsFeatureFlag([FEATURES.IS_OPTIONCHAIN]);
  const isSimulator = useIsFeatureFlag([FEATURES.IS_SIMULATOR]);
  const isTemplates = useIsFeatureFlag([FEATURES.IS_TEMPLATES]);
  const isBots = useIsFeatureFlag([FEATURES.IS_BOTS]);
  const isMyPortfolio = useIsFeatureFlag([FEATURES.IS_MY_PORTFOLIO]);
  const isReport = useIsFeatureFlag([FEATURES.IS_REPORT]);
  const isHomePage = useIsFeatureFlag([FEATURES.IS_HOME_PAGE]);
  const isTradeIdeas = useIsFeatureFlag([FEATURES.IS_TRADE_IDEAS]);
  const isPineSignals = useIsFeatureFlag([FEATURES.IS_PINE_SIGNALS]);
  const isNewBotPortfolio = useIsFeatureFlag([FEATURES.IS_NEW_BOT_PORTFOLIO]);
  const isMybotsInPortfolio = useIsFeatureFlag([
    FEATURES.IS_MYBOTS_IN_PORTFOLIO,
  ]);
  const isCharts = useIsFeatureFlag([FEATURES.IS_CHARTS]);
  const isCommunity = useIsFeatureFlag([FEATURES.IS_COMMUNITY]);
  const isAiBots = useIsFeatureFlag([FEATURES.IS_AI_BOTS]);
  const isDarkThemeFeature = useIsFeatureFlag([FEATURES.IS_DARK_THEME_FEATURE]);
  const isRABots = useIsFeatureFlag([FEATURES.IS_RA_BOTS as FEATURES]);
  const { data: siteInfo } = useGetSiteInfo();
  const theme = useTheme();

  // const [darkMode, setDarkMode] = useState(false);

  // const handleThemeChange = () => {
  //   setDarkMode(!darkMode);
  // };
  const { isDarkTheme, toggleTheme } = useContext(themeContext);
  const handleNavMenuClick = (value: string) => {
    const internalRoute =
      value.split(',').length > 1 ? toBoolean(value.split(',')[1]) : true;
    const url = value.split(',').length > 0 ? value.split(',')[2] : '';
    if (internalRoute) {
      if (value === 'login') loginModal(true);
      else history.push(`/${value}`);
    } else {
      openInNewTab(url ?? '');
    }
  };

  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    setShowTooltip(true);
    const timer = setTimeout(() => {
      setShowTooltip(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleLaunchClick = () => {
    window.open(
      siteInfo?.doc_url
        ? siteInfo.doc_url
        : 'https://docs.tradesignal.tech/docs/getting-started',
      '_blank',
      'noopener,noreferrer',
    );
  };
  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'space-between'}
      height={'100%'}
    >
      <List>
        {isHomePage && (
          <MenuListButton
            open={open}
            label="Home"
            icon={activeNav === 'home' ? <HomeIcon /> : <HomeOutlinedIcon />}
            activeMenu={activeNav === 'home'}
            setValue={handleNavMenuClick}
            value="home"
          />
        )}
        {isLoggedIn && isNewBotPortfolio && (
          <MenuListButton
            label={`${isMyPortfolio ? 'My ' : ''}Bots`}
            icon={
              activeNav === 'portfolio' ? (
                <LeaderboardIcon />
              ) : (
                <LeaderboardOutlinedIcon />
              )
            }
            value="portfolio"
            activeMenu={activeNav === 'portfolio'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && (
          <MenuListButton
            label={`${isMyPortfolio ? 'My ' : ''}Bots`}
            icon={
              activeNav === 'portfolio' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <LeaderboardIcon />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <LeaderboardIcon />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'portfolio'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isAiBots && (
          <MenuListButton
            label="AI Bots"
            icon={activeNav === 'ai/bots' ? <SmartToy /> : <SmartToyOutlined />}
            value="ai/bots"
            activeMenu={activeNav === 'ai/bots'}
            setValue={handleNavMenuClick}
            open={open}
            chip={
              <MuiChip
                label="Beta"
                size="small"
                color="info"
                sx={{ height: '20px', fontSize: '12px', fontWeight: 'bold' }}
              />
            }
          />
        )}
        {!isLoggedIn && isAiBots && (
          <MenuListButton
            label="AI Bots"
            icon={
              activeNav === 'ai/bots' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <SmartToy />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <SmartToyOutlined />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'ai/bots'}
            setValue={handleNavMenuClick}
            open={open}
            chip={
              <MuiChip
                label="Beta"
                size="small"
                color="infolight"
                sx={{ height: '20px', fontSize: '10px' }}
              />
            }
          />
        )}
        {isLoggedIn && isDiscover && (
          <MenuListButton
            label={'Discover'}
            icon={
              activeNav === 'discover' ? <StoreIcon /> : <StoreOutlinedIcon />
            }
            value="discover/bots"
            activeMenu={activeNav === 'discover'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isDiscover && (
          <MenuListButton
            label={'Discover'}
            icon={
              activeNav === 'discover' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <StoreIcon />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <StoreOutlinedIcon />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'discover'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isRABots && (
          <MenuListButton
            label={'RA Bots'}
            icon={activeNav === 'ra' ? <StoreIcon /> : <StoreOutlinedIcon />}
            value="ra/bots"
            activeMenu={activeNav === 'ra'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isRABots && (
          <MenuListButton
            label={'RA Bots'}
            icon={
              activeNav === 'ra' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <StoreIcon />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <StoreOutlinedIcon />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'ra'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isTraders && (
          <MenuListButton
            label="Traders"
            icon={
              activeNav === 'traders' ? <PeopleIcon /> : <PeopleOutlinedIcon />
            }
            value="traders"
            activeMenu={activeNav === 'traders'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isTraders && (
          <MenuListButton
            label="Traders"
            icon={
              activeNav === 'traders' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <PeopleIcon />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <PeopleOutlinedIcon />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'traders'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isBots && !isMybotsInPortfolio && (
          <MenuListButton
            label="My Bots"
            icon={
              activeNav === 'bots' ? <WidgetsIcon /> : <WidgetsOutlinedIcon />
            }
            value="bots"
            activeMenu={activeNav === 'bots'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isBots && !isMybotsInPortfolio && (
          <MenuListButton
            label="My Bots"
            icon={
              activeNav === 'bots' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <WidgetsIcon />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <WidgetsOutlinedIcon />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'bots'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isSimulator && (
          <MenuListButton
            label="Simulator"
            icon={activeNav === 'simulator' ? <QueryStats /> : <QueryStatsOutlined />}
            value="simulator"
            activeMenu={activeNav === 'simulator'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isSimulator && (
          <MenuListButton
            label="Simulator"
            icon={
              activeNav === 'simulator' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <QueryStats />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <QueryStatsOutlined />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'simulator'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isOptionChain && (
          <MenuListButton
            label="Option Chain"
            icon={activeNav === 'option-chain' ? <Link /> : <LinkOutlined />}
            value="option-chain"
            activeMenu={activeNav === 'option-chain'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isOptionChain && (
          <MenuListButton
            label="Option Chain"
            icon={
              activeNav === 'option-chain' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <Link />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <LinkOutlined />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'option-chain'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isTradeIdeas && (
          <MenuListButton
            label="Trade Ideas"
            icon={
              activeNav === 'trade-options' ? (
                <TipsAndUpdates />
              ) : (
                <TipsAndUpdatesOutlined />
              )
            }
            value="trade-options"
            activeMenu={activeNav === 'trade-options'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isTradeIdeas && (
          <MenuListButton
            label="Trade Ideas"
            icon={
              activeNav === 'trade-options' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <TipsAndUpdates />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <TipsAndUpdatesOutlined />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'trade-options'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isCharts && (
          <MenuListButton
            label="Charts"
            icon={activeNav === 'charts' ? <CandlestickChart /> : <CandlestickChartOutlined />}
            value="charts"
            activeMenu={activeNav === 'charts'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isCharts && (
          <MenuListButton
            label="Charts"
            icon={
              activeNav === 'charts' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <CandlestickChart />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <CandlestickChartOutlined />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'charts'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isCommunity && (
          <MenuListButton
            label="Community"
            icon={activeNav === 'community' ? <CandlestickChart /> : <CandlestickChartOutlined />}
            value="community"
            activeMenu={activeNav === 'community'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isCommunity && (
          <MenuListButton
            label="Community"
            icon={
              activeNav === 'community' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <CandlestickChart />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <CandlestickChartOutlined />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'community'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isReport && (
          <MenuListButton
            label="Report"
            icon={
              activeNav === 'report' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <Assessment />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <AssessmentOutlined />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'report'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isTemplates && (
          <MenuListButton
            label="Templates"
            icon={
              activeNav === 'templates' ? (
                <WidgetsIcon />
              ) : (
                <WidgetsOutlinedIcon />
              )
            }
            value="templates"
            activeMenu={activeNav === 'templates'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && isPineSignals && (
          <MenuListButton
            label="Signals"
            icon={
              activeNav === 'signal' ? (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <Sensors />
                </Badge>
              ) : (
                <Badge badgeContent={<LockIcon style={{ fontSize: '17' }} />}>
                  <Sensors />
                </Badge>
              )
            }
            value="login"
            activeMenu={activeNav === 'signal'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isPineSignals && (
          <MenuListButton
            label="Signals"
            icon={activeNav === 'signal' ? <Sensors /> : <Sensors />}
            value="signal"
            activeMenu={activeNav === 'signal'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && (
          <MenuListButton
            label="Brokers"
            icon={
              activeNav === 'broker' ? <DomainAdd /> : <DomainAddOutlined />
            }
            value="broker"
            activeMenu={activeNav === 'broker'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isLoggedIn && isReport && (
          <MenuListButton
            label="Report"
            icon={
              activeNav === 'report' ? <Assessment /> : <AssessmentOutlined />
            }
            value="report"
            activeMenu={activeNav === 'report'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}

        {isLoggedIn && (
          <MenuListButton
            label="Settings"
            icon={activeNav === 'menu' ? <Settings /> : <SettingsOutlined />}
            value="menu"
            activeMenu={activeNav === 'menu'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {!isLoggedIn && (
          <MenuListButton
            label="Sign In"
            icon={<LoginIcon />}
            value="login"
            activeMenu={activeNav === 'menu'}
            setValue={handleNavMenuClick}
            open={open}
          />
        )}
        {isDarkThemeFeature && (
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
              }}
              onClick={toggleTheme}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 'auto',
                  justifyContent: 'center',
                }}
                color="inherit"
              >
                {/* <IconButton> */}
                {isDarkTheme ? <Brightness4Icon /> : <Brightness7Icon />}
                {/* </IconButton> */}
              </ListItemIcon>

              <ListItemText
                primary={'Theme'}
                sx={{
                  opacity: open ? 1 : 0,
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      <List>
        <Divider orientation="horizontal" />

        {siteInfo && siteInfo.doc_url && (
          <Tooltip
            title="Click here to read the docs"
            open={showTooltip}
            arrow
            placement="top"
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 300 }}
          >
            <div style={{ position: 'relative' }}>
              <div
                className="docs-button"
                style={{
                  borderRadius: '12px',
                  padding: '12px',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  cursor: 'pointer',
                }}
                onClick={handleLaunchClick}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {/* Icon Container */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      width: '40px',
                      height: '40px',
                      backgroundColor: theme.palette.custom.docBgColor,
                      margin: open ? 1 : 'auto',
                      transition: 'background-color 0.3s ease',
                    }}
                  >
                    <Description
                      sx={{
                        width: 20,
                        height: 20,
                        color: theme.palette.custom.docColor,
                      }}
                    />
                  </div>
                  {open && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: open ? 1 : 0,
                        width: '80%',
                      }}
                    >
                      {/* Content */}
                      <div style={{ flexGrow: 1 }}>
                        <h6
                          style={{
                            marginBottom: '4px',
                            fontWeight: '500',
                            fontSize: '14px',
                            margin: 0,
                          }}
                        >
                          Documentation
                        </h6>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '12px',
                          }}
                        >
                          Get started guide
                        </p>
                      </div>

                      {/* Launch Icon */}
                      <Launch
                        sx={{
                          width: 20,
                          height: 20,
                          cursor: 'pointer',
                        }}
                        onClick={handleLaunchClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleLaunchClick();
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Tooltip>
        )}
      </List>
    </Box>
  );
};

export default memo(SideBarNavigationMenu);

const MenuListButton = ({
  label,
  open,
  icon,
  activeMenu,
  setValue,
  value,
  url,
  chip,
}: {
  label: string;
  open: boolean;
  icon: ReactJSXElement;
  activeMenu: boolean;
  setValue: (value: string) => void;
  value: string;
  url?: string;
  chip?: React.ReactNode;
}) => {
  const theme = useTheme();
  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <ListItemButton
        sx={{
          minHeight: 48,
          justifyContent: open ? 'initial' : 'center',
          px: 2.5,
        }}
        onClick={() => setValue(`${value}${url ? `,false,${url}` : ''}`)}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 2 : 'auto',
            justifyContent: 'center',
            color: activeMenu ? theme.palette.common.black : '',
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {label}
              {chip && open && chip}
            </Box>
          }
          sx={{
            opacity: open ? 1 : 0,
            color: activeMenu
              ? theme.palette.common.black
              : theme.palette.grey[600],
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};
